// ============================================================
// Chess Arena — Screen Manager
// Manages HTML/CSS screen transitions for the hybrid UI layer
// ============================================================

export interface Screen {
  name: string;
  init(container: HTMLElement): void;
  show(data?: Record<string, unknown>): void;
  hide(): void;
  update?(): void;
  destroy?(): void;
}

export class ScreenManager {
  private screens: Map<string, Screen> = new Map();
  private containers: Map<string, HTMLElement> = new Map();
  private activeScreen: string | null = null;
  private root: HTMLElement;
  private updateInterval: number | null = null;

  constructor(rootId: string = 'ui-root') {
    let root = document.getElementById(rootId);
    if (!root) {
      root = document.createElement('div');
      root.id = rootId;
      document.body.appendChild(root);
    }
    this.root = root;

    // Start update loop for active screens (e.g., timers)
    this.updateInterval = window.setInterval(() => this.tick(), 1000);
  }

  /** Register a screen instance. Creates a container div and calls init(). */
  register(screen: Screen): void {
    if (this.screens.has(screen.name)) {
      console.warn(`[ScreenManager] Screen "${screen.name}" already registered.`);
      return;
    }

    const container = document.createElement('div');
    container.id = `screen-${screen.name}`;
    container.className = 'ca-screen hidden';
    container.setAttribute('data-screen', screen.name);
    this.root.appendChild(container);

    screen.init(container);

    this.screens.set(screen.name, screen);
    this.containers.set(screen.name, container);
  }

  /** Show a screen by name with optional data. Hides the current active screen. */
  showScreen(name: string, data?: Record<string, unknown>): void {
    const screen = this.screens.get(name);
    const container = this.containers.get(name);
    if (!screen || !container) {
      console.error(`[ScreenManager] Screen "${name}" not found.`);
      return;
    }

    // Hide current screen if it's a different one
    if (this.activeScreen && this.activeScreen !== name) {
      this.hideScreen(this.activeScreen);
    }

    // Show the target screen
    container.classList.remove('hidden');
    // Force reflow to trigger CSS transition
    void container.offsetHeight;
    container.classList.add('active');

    screen.show(data);
    this.activeScreen = name;
  }

  /** Hide a specific screen by name. */
  hideScreen(name: string): void {
    const screen = this.screens.get(name);
    const container = this.containers.get(name);
    if (!screen || !container) return;

    container.classList.remove('active');
    container.classList.add('hidden');
    screen.hide();

    if (this.activeScreen === name) {
      this.activeScreen = null;
    }
  }

  /** Hide all registered screens. */
  hideAll(): void {
    for (const name of this.screens.keys()) {
      this.hideScreen(name);
    }
    this.activeScreen = null;
  }

  /** Get the currently active screen name. */
  getActiveScreen(): string | null {
    return this.activeScreen;
  }

  /** Get a screen instance by name. */
  getScreen<T extends Screen>(name: string): T | undefined {
    return this.screens.get(name) as T | undefined;
  }

  /** Called every second to update active screens (timers, etc.). */
  private tick(): void {
    if (this.activeScreen) {
      const screen = this.screens.get(this.activeScreen);
      screen?.update?.();
    }
  }

  /** Clean up all screens. */
  destroy(): void {
    if (this.updateInterval !== null) {
      clearInterval(this.updateInterval);
      this.updateInterval = null;
    }
    for (const [name, screen] of this.screens) {
      screen.destroy?.();
      const container = this.containers.get(name);
      container?.remove();
    }
    this.screens.clear();
    this.containers.clear();
    this.activeScreen = null;
  }
}

// Singleton instance
let instance: ScreenManager | null = null;

export function getScreenManager(): ScreenManager {
  if (!instance) {
    instance = new ScreenManager();
  }
  return instance;
}
